import { useId } from 'react';
import { MascotBust } from './mascot';
import { cx } from './styles';

const markSizes = { sm: 'size-8', md: 'size-9', lg: 'size-11' };
const wordSizes = { sm: 'text-lg', md: 'text-xl', lg: 'text-[1.75rem]' };

type BrandTone = 'dark' | 'light';

const tiles: Record<BrandTone, string> = { dark: '#2394ea', light: '#ffffff' };

const wordTones: Record<BrandTone, { word: string; subtitle: string }> = {
  dark: { word: 'text-brand-950', subtitle: 'text-slate-500' },
  light: { word: 'text-white', subtitle: 'text-white/80' },
};

interface BrandMarkProps {
  readonly size?: keyof typeof markSizes;
  readonly tone?: BrandTone;
  readonly className?: string;
}

export function BrandMark({ size = 'md', tone = 'dark', className }: BrandMarkProps) {
  const id = useId();
  return (
    <svg
      viewBox="122 62 442 442"
      aria-hidden="true"
      className={cx(markSizes[size], 'shrink-0 drop-shadow-sm', className)}
    >
      <clipPath id={`${id}tile`}>
        <rect x="122" y="62" width="442" height="442" rx="120" />
      </clipPath>
      <rect x="122" y="62" width="442" height="442" rx="120" fill={tiles[tone]} />
      <g clipPath={`url(#${id}tile)`}>
        <MascotBust clipId={`${id}tail`} />
      </g>
    </svg>
  );
}

interface BrandProps {
  readonly size?: keyof typeof markSizes;
  readonly tone?: BrandTone;
  readonly subtitle?: string;
  readonly className?: string;
}

export function Brand({ size = 'md', tone = 'dark', subtitle, className }: BrandProps) {
  return (
    <span className={cx('flex items-center gap-2.5', className)}>
      <BrandMark size={size} tone={tone} />
      <span className="leading-none">
        <span
          className={cx(
            'block font-rounded font-semibold tracking-tight',
            wordTones[tone].word,
            wordSizes[size],
          )}
        >
          Oefy
        </span>
        {subtitle ? (
          <span className={cx('mt-0.5 block text-xs', wordTones[tone].subtitle)}>{subtitle}</span>
        ) : null}
      </span>
    </span>
  );
}
